/**
 * Suggested for you (TMMAPP-0152 §A 6; P:132–143, data P:1329–1334): four tiles per intent, each a
 * 40 px icon square in its categorical tint, a heading-face title and a line. Every tile is real or
 * opens the Coming soon dialog; its line is served data or says how to start (never sample copy).
 *
 * - An assistant tile starts a **private** session (`useAskAssistant`) whose composer is prefilled
 *   with the tile's question; nothing is sent until the person sends it.
 * - A subscriber-only target (an assistant session, My Accounts, an account, Recurring) goes through
 *   `useSubscriberAction()` for a GUEST or NONE tier: the purchase experience, never a dead end.
 * - **No silent tiles** (TMMAPP-0154 item 27): in Just me the assistant tiles read every account the
 *   person owns. While those load they are busy (`aria-busy`) and a click waits for them; when the
 *   read fails they show the catalog error with Retry.
 * - **A space's data, never your own books** (MM-D-43; item 28): in a space, Ask about a bill and
 *   Bills & paychecks read the space's next outgoing plan event, Review recent activity its
 *   register's latest line, each with its "none" line; My Accounts is a link with no own-books line.
 */
import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Box, Button, ButtonBase, Skeleton, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import type { AssistantScopeEntity } from '@/api/assistant';
import type { MeetingCalendarEntry } from '@/api/meetings';
import {
  AssistantNavIcon,
  AssistantSparkleNavIcon,
  CommunityNavIcon,
  HeartIcon,
  HelpNavIcon,
  LeafIcon,
  MeetingsIcon,
  RuleIcon,
  ToolsNavIcon,
  VideoIcon,
  VirtualAccountsIcon,
  type IconProps,
} from '@/components/icons';
import { useAskAssistant } from '@/features/assistant/useAskAssistant';
import { formatCivilDate, formatCivilDateNear, formatList } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import type { HomeScope } from './homeScope';
import { tintFor, type TintName } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import type { FocusView } from './homeFocus';
import { meetingDateTime, shortDate } from './homeFormat';
import { meetingSessionPath } from '@/features/meetings/nextMeeting';
import { nextOutgoingOfPlan, type Intent } from './homeRules';
import { dataOf, type HomeData } from './useHomeData';

interface Tile {
  key: string;
  title: string;
  /** The line: served data, how to start, or a skeleton while it loads. */
  body: ReactNode;
  tint: TintName;
  icon: ComponentType<IconProps>;
  run: () => void;
  subscriberOnly?: boolean;
  /** Waiting for what it reads: `aria-busy`, and a click waits for it. */
  busy?: boolean;
  /** What it reads failed: the catalog error with Retry, in place of the line. */
  error?: { message: string; onRetry: () => void } | undefined;
}

function SuggestionTile({ tile }: { tile: Tile }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const action = useSubscriberAction(() => tile.run());
  const Icon = tile.icon;
  const tint = tintFor(tile.tint, tokens);
  const tileSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: '10px',
    p: '18px',
    minWidth: 0,
    borderRadius: '18px',
    border: '1px solid',
    borderColor: tokens.line,
    bgcolor: tokens.surface,
    color: tokens.text,
    textAlign: 'start',
  } as const;
  const icon = (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 40,
        height: 40,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: tint.soft,
        color: tokens.text,
      }}
    >
      <Icon />
    </Box>
  );
  // A failed read: the tile is no button (a Retry can't sit inside one); it says why, with Retry.
  if (tile.error !== undefined) {
    return (
      <Box data-suggestion={tile.key} data-suggestion-error="" sx={tileSx}>
        {icon}
        <Typography variant="h5" component="span" sx={{ overflowWrap: 'anywhere' }}>
          {tile.title}
        </Typography>
        <Typography
          component="span"
          role="alert"
          data-suggestion-body=""
          sx={{ width: '100%', fontSize: 13, lineHeight: '18px', color: tokens.text2 }}
        >
          {tile.error.message}
        </Typography>
        <Button size="small" variant="outlined" onClick={tile.error.onRetry}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }
  return (
    <>
      <ButtonBase
        onClick={tile.subscriberOnly === true ? action.run : () => tile.run()}
        aria-describedby={tile.subscriberOnly === true ? action.descriptionId : undefined}
        aria-busy={tile.busy === true || undefined}
        data-suggestion={tile.key}
        sx={{
          ...tileSx,
          '&:hover': { borderColor: tokens.lineStrong },
          '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
        }}
      >
        {icon}
        <Typography variant="h5" component="span" sx={{ overflowWrap: 'anywhere' }}>
          {tile.title}
        </Typography>
        <Typography
          component="span"
          data-suggestion-body=""
          sx={{
            width: '100%',
            fontSize: 13,
            lineHeight: '18px',
            color: tokens.textLabel('surface'),
            overflowWrap: 'anywhere',
          }}
        >
          {tile.body}
        </Typography>
      </ButtonBase>
      {tile.subscriberOnly === true && (
        <SubscriberActionHint id={action.descriptionId} description={action.description} />
      )}
    </>
  );
}

const loadingLine = <Skeleton variant="text" width="80%" />;

/** The most recently imported of the person's own accounts, with that import's date. */
function latestImport(accounts: readonly Account[]): { account: Account; at: string } | null {
  let best: { account: Account; at: string } | null = null;
  for (const account of accounts) {
    const at = account.lastImport?.at ?? null;
    if (at !== null && (best === null || at > best.at)) best = { account, at };
  }
  return best;
}

export function HomeSuggestions({
  intent,
  scope,
  data,
  ownAccounts,
  nextMeeting,
  focus,
  onComingSoon,
}: {
  intent: Intent;
  scope: HomeScope;
  data: HomeData;
  focus: FocusView | null | undefined;
  /** The person's own open accounts; null while they load or for a guest. */
  ownAccounts: Account[] | null;
  /** The next meeting in scope; undefined while it loads. */
  nextMeeting: MeetingCalendarEntry | null | undefined;
  onComingSoon: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const navigate = useNavigate();
  const { ask } = useAskAssistant();
  const zone = me.user.timezone ?? 'UTC';
  const go = (to: string) => () => void navigate(to);
  const space = scope.kind === 'space' ? scope.virtualAccount : null;

  // The assistant reads what the tile is about: the space, or every account the person owns.
  // `null` while Just me's accounts load: the tile waits rather than start a session with no scope.
  const askScope: AssistantScopeEntity[] | null =
    space !== null
      ? [{ kind: 'VIRTUAL_ACCOUNT', id: space.id }]
      : (ownAccounts?.map((account) => ({ kind: 'ACCOUNT' as const, id: account.id })) ?? null);
  // No silent tiles (item 27): Just me's accounts read failed, or is still loading.
  const ownAccountsRead = space === null && me.tier === 'SUBSCRIBER' ? data.accounts.read : null;
  const ownFailed = ownAccountsRead?.status === 'error';
  const ownBusy = ownAccountsRead?.status === 'loading';
  const ownError = ownFailed
    ? {
        message: t('errors.loadTitle', { what: t('errors.what.accounts') }),
        onRetry: data.accounts.retry,
      }
    : undefined;
  // A click while they load waits for them, then starts the session with its scope; a failed read
  // drops it (the tile then says why).
  const [pending, setPending] = useState<{ name: string; question: string | null } | null>(null);
  if (pending !== null && ownFailed) setPending(null);
  // Each pending click starts once (an effect may run again before the start lands).
  const started = useRef<object | null>(null);
  const latest = useRef({ askScope, ask });
  useEffect(() => {
    latest.current = { askScope, ask };
  });
  const scopeReady = askScope !== null;
  useEffect(() => {
    const { askScope: readyScope, ask: start } = latest.current;
    if (pending === null || !scopeReady || readyScope === null) return;
    if (started.current === pending) return;
    started.current = pending;
    void start({ name: pending.name, scope: readyScope, question: pending.question });
  }, [pending, scopeReady]);
  const askTile = (name: string, question: string | null) => () => {
    if (askScope !== null) void ask({ name, scope: askScope, question });
    else if (ownBusy) setPending({ name, question });
  };

  // The next bill: the space's next outgoing plan event in a space, the person's own summary in
  // Just me (MM-D-43).
  const summary = dataOf(data.summary.read);
  const plan = dataOf(data.plan.read);
  const next =
    space !== null
      ? plan === null
        ? null
        : nextOutgoingOfPlan(plan)
      : (summary?.nextPlanned ?? null);
  const nextDate =
    next === null ? null : formatCivilDateNear(next.occursOn, locale, todayCivilInZone(zone));
  const summaryLoading =
    space !== null
      ? data.plan.read.status === 'loading'
      : data.summary.read.status === 'loading' && me.tier === 'SUBSCRIBER';
  // A space's failed read is an error with Retry, never its "none" line (items 27–28).
  const spaceError = (read: { status: string }, retry: () => void, what: string) =>
    space !== null && read.status === 'error'
      ? { message: t('errors.loadTitle', { what: t(what) }), onRetry: retry }
      : undefined;
  const planError = spaceError(data.plan.read, data.plan.retry, 'errors.what.cashflow');
  const ledgerError = spaceError(
    data.ledger.read,
    data.ledger.retry,
    'errors.what.virtualAccountRegister',
  );

  const tiles = (): Tile[] => {
    switch (intent) {
      case 'support':
        return [
          {
            key: 'talk',
            title: t('home.suggest.talk.title'),
            body: t('home.suggest.talk.body'),
            tint: 'sage',
            icon: AssistantSparkleNavIcon,
            run: askTile(t('home.suggest.talk.title'), null),
            subscriberOnly: true,
            busy: ownBusy,
            error: ownError,
          },
          {
            key: 'breath',
            title: t('home.suggest.breath.title'),
            body: t('home.suggest.breath.body'),
            tint: 'peach',
            icon: LeafIcon,
            run: onComingSoon,
          },
          {
            key: 'live',
            title: t('home.suggest.live.title'),
            body: t('home.suggest.live.body'),
            tint: 'lavender',
            icon: VideoIcon,
            run: go('/community'),
          },
          {
            key: 'people',
            title: t('home.suggest.people.title'),
            body: t('home.suggest.people.body'),
            tint: 'sky',
            icon: CommunityNavIcon,
            run: go('/community'),
          },
        ];
      case 'understand': {
        const billQuestion =
          next?.payee != null
            ? t('home.suggest.bill.question', { payee: next.payee })
            : t('home.suggest.bill.questionAny');
        return [
          {
            key: 'changed',
            title: t('home.suggest.changed.title'),
            body: t('home.suggest.changed.body'),
            tint: 'peach',
            icon: VirtualAccountsIcon,
            run: askTile(t('home.suggest.changed.title'), t('home.suggest.changed.question')),
            subscriberOnly: true,
            busy: ownBusy,
            error: ownError,
          },
          {
            key: 'bill',
            title: t('home.suggest.bill.title'),
            body: summaryLoading
              ? loadingLine
              : next === null || nextDate === null
                ? t('home.suggest.bill.bodyNone')
                : next.payee === null
                  ? t('home.suggest.bill.bodyNoPayee', { date: nextDate })
                  : t('home.suggest.bill.body', { payee: next.payee, date: nextDate }),
            tint: 'sky',
            icon: HelpNavIcon,
            run: askTile(t('home.suggest.bill.title'), billQuestion),
            subscriberOnly: true,
            busy: ownBusy,
            error: ownError ?? planError,
          },
          {
            key: 'picture',
            title: t('home.suggest.picture.title'),
            body: t('home.suggest.picture.body'),
            tint: 'sage',
            icon: ToolsNavIcon,
            run: go('/me/tools'),
          },
          {
            key: 'topic',
            title: t('home.suggest.topic.title'),
            body: t('home.suggest.topic.body'),
            tint: 'lavender',
            icon: MeetingsIcon,
            run: go(meetingSessionPath(nextMeeting ?? null)),
          },
        ];
      }
      case 'done': {
        const imported = ownAccounts === null || space !== null ? null : latestImport(ownAccounts);
        // A space's register, its latest line (item 28).
        const latest = space === null ? null : (dataOf(data.ledger.read)?.items[0] ?? null);
        const envelopes = dataOf(data.envelopes.read)?.filter((row) => row.archivedAt === null);
        const names = envelopes?.slice(0, 3).map((row) => row.name) ?? [];
        return [
          {
            key: 'activity',
            title: t('home.suggest.activity.title'),
            body:
              space !== null
                ? data.ledger.read.status === 'loading'
                  ? loadingLine
                  : latest === null
                    ? t('home.suggest.activity.bodyNone')
                    : t('home.suggest.activity.bodySpace', {
                        payee: latest.payee,
                        date: formatCivilDate(latest.postedOn, locale, {
                          month: 'short',
                          day: 'numeric',
                        }),
                      })
                : ownAccounts === null && summaryLoading
                  ? loadingLine
                  : imported === null
                    ? t('home.suggest.activity.bodyNone')
                    : t('home.suggest.activity.body', {
                        account: imported.account.name,
                        date: shortDate(imported.at, locale, zone),
                      }),
            tint: 'sky',
            icon: RuleIcon,
            run: go(
              space !== null
                ? `/v/${space.id}`
                : imported === null
                  ? '/me/accounts'
                  : `/me/accounts/${imported.account.id}`,
            ),
            subscriberOnly: space === null,
            error: ledgerError,
          },
          {
            key: 'bills',
            title: t('home.suggest.bills.title'),
            body: summaryLoading
              ? loadingLine
              : next === null || nextDate === null
                ? t('home.suggest.bills.bodyNone')
                : next.payee === null
                  ? t('home.suggest.bills.bodyNoPayee', { date: nextDate })
                  : t('home.suggest.bills.body', { payee: next.payee, date: nextDate }),
            tint: 'rose',
            icon: MeetingsIcon,
            // A space's bills are its plan, on its Forecast tab; Just me's are Recurring.
            run: go(
              space === null ? '/me/recurring?tab=transactions' : `/v/${space.id}?tab=forecast`,
            ),
            subscriberOnly: space === null,
            error: planError,
          },
          {
            key: 'accounts',
            title: t('home.suggest.accounts.title'),
            // In a space, a link with no own-books line (MM-D-43).
            body:
              space !== null
                ? t('home.suggest.accounts.bodyNone')
                : ownAccounts === null
                  ? summaryLoading
                    ? loadingLine
                    : t('home.suggest.accounts.bodyNone')
                  : ownAccounts.length === 0
                    ? t('home.suggest.accounts.bodyNone')
                    : t('home.suggest.accounts.body', { count: ownAccounts.length }),
            tint: 'sage',
            icon: ToolsNavIcon,
            run: go('/me/accounts'),
            subscriberOnly: true,
          },
          {
            key: 'envelopes',
            title: t('home.suggest.envelopes.title'),
            body:
              space !== null && data.envelopes.read.status === 'loading'
                ? loadingLine
                : space === null || names.length === 0
                  ? t('home.suggest.envelopes.bodyNone')
                  : (envelopes?.length ?? 0) > 3
                    ? t('home.suggest.envelopes.bodyMore', {
                        first: names[0],
                        second: names[1],
                        third: names[2],
                      })
                    : t('home.suggest.envelopes.body', { names: formatList(names, locale) }),
            tint: 'peach',
            icon: VirtualAccountsIcon,
            run: go(space === null ? '/me/virtual-accounts' : `/v/${space.id}`),
          },
        ];
      }
      case 'connect': {
        const sessions = dataOf(data.sessions.read);
        const shared =
          sessions?.find(
            (row) =>
              row.visibility === 'SHARED' && (space === null || row.virtualAccountId === space.id),
          ) ?? null;
        const when = nextMeeting == null ? null : meetingDateTime(nextMeeting, locale, zone);
        return [
          {
            key: 'meeting',
            title: t('home.suggest.meeting.title'),
            body:
              nextMeeting === undefined
                ? loadingLine
                : when === null
                  ? t('home.suggest.meeting.bodyNone')
                  : t('home.suggest.meeting.body', when),
            tint: 'peach',
            icon: MeetingsIcon,
            run: go(meetingSessionPath(nextMeeting ?? null)),
          },
          {
            key: 'shared',
            title: t('home.suggest.shared.title'),
            body:
              data.sessions.read.status === 'loading'
                ? loadingLine
                : shared === null
                  ? t('home.suggest.shared.bodyNone')
                  : shared.unreadCount > 0
                    ? t('home.suggest.shared.body', {
                        session: shared.name,
                        count: shared.unreadCount,
                      })
                    : shared.name,
            tint: 'sage',
            icon: AssistantNavIcon,
            run: go(shared === null ? '/me/assistant' : `/me/assistant/${shared.id}`),
          },
          {
            key: 'focus',
            title: t('home.suggest.focus.title'),
            body:
              focus === undefined
                ? loadingLine
                : focus === null
                  ? t('home.suggest.focus.bodyNone')
                  : (focus.line ?? focus.name),
            tint: 'sky',
            icon: HeartIcon,
            run: go(focus == null ? '/me/meetings' : focus.to),
          },
          {
            key: 'starter',
            title: t('home.suggest.starter.title'),
            body: t('home.suggest.starter.body'),
            tint: 'lavender',
            icon: LeafIcon,
            run: onComingSoon,
          },
        ];
      }
    }
  };

  return (
    <Box
      data-suggestions={intent}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
        gap: '14px',
      }}
    >
      {tiles().map((tile) => (
        <SuggestionTile key={tile.key} tile={tile} />
      ))}
    </Box>
  );
}
